import { isAfter, isBefore } from 'date-fns';
import { z } from 'zod';

import { isDefined, isEmpty, isNotDefined } from '@hsp/utilities';

import { AudienceCategory, AudienceOrderType, AudienceOrderTypeSchema, AudienceStatus } from 'types/api/audience';
import { ClientSchema, IClient } from 'types/api/client';
import { TagSchema } from 'types/api/tag';
import { s } from 'utilities';

export const SaveFormTypeSchema = z.union([
  z.literal('audience'),
  z.literal('audience-pre-built'),
  z.literal('audience-master-file'),
  z.literal('basic'),
  z.literal('master-file'),
  z.literal('pre-built'),
]);

export const AudienceSchema = z.object({
  client: z.preprocess(
    (arg, context) => {
      if (isNotDefined(arg) || isEmpty(arg)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: s(['Site.Pages.AudienceBuilder.Save.Validation.Client.Required']),
        });

        return z.NEVER;
      }

      const { id, name } = arg as IClient;

      if (isNotDefined(id) || isNotDefined(name)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: s(['Site.Pages.AudienceBuilder.Save.Validation.Client.Required']),
        });

        return z.NEVER;
      }

      return arg;
    },
    ClientSchema.superRefine((client, context) => {
      const { id, name } = client;

      if (isNotDefined(id) || isNotDefined(name)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: s(['Site.Pages.AudienceBuilder.Save.Validation.Client.Required']),
        });

        return z.NEVER;
      }

      return client;
    })
  ),
  giftDateEnd: z.date().nullish(),
  giftDateStart: z.date().nullish(),
  id: z.string().optional().nullish(),
  name: z
    .string({
      invalid_type_error: s(['Site.Pages.AudienceBuilder.Save.Validation.Name.DropDown']),
      required_error: s(['Site.Pages.AudienceBuilder.Save.Validation.Name.DropDown']),
    })
    .trim()
    .nonempty({ message: s(['Site.Pages.AudienceBuilder.Save.Validation.Name.Input']) }),
  orderNumber: z.string().nonempty({ message: 'Order is required' }),
  orderType: AudienceOrderTypeSchema,
  type: z.literal('audience'),
});

export const AudiencePreBuiltSchema = AudienceSchema.extend({
  orderNumber: z.string().optional(),
  type: z.literal('audience-pre-built'),
});

export const AudienceMasterFileSchema = AudienceSchema.omit({ orderNumber: true }).extend({
  type: z.literal('audience-master-file'),
});

const BaseSchema = AudienceSchema.omit({ orderType: true }).extend({
  autoTag: z.array(z.string()).optional(),
  categoryId: z.nativeEnum(AudienceCategory),
  description: z.string().nullable().default(null),
  mailCode: z
    .string()
    .trim()
    .nonempty({ message: s(['Site.Pages.AudienceBuilder.Save.Validation.MailCode.Required']) }),
  orderTypeId: z.preprocess((arg, context) => {
    if (isNotDefined(arg) || isEmpty(arg)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: s(['Site.Pages.AudienceBuilder.Save.Validation.OrderType.Required']),
      });

      return z.NEVER;
    }

    if (arg === AudienceOrderType.NotSet) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: s(['Site.Pages.AudienceBuilder.Save.Validation.OrderType.Required']),
      });

      return z.NEVER;
    }

    return arg;
  }, z.nativeEnum(AudienceOrderType)),
  parameters: z.any(),
  primarySelect: z.string().nullable(),
  quantity: z.number().gte(0, 'Quantity must be 0 or greater').default(0),
  secondarySelect: z.string().nullable(),
  sequenceNumber: z.number().gte(1, 'Sequence number should be 1 or greater').default(1),
  shopId: z.string().nullish(),
  statusId: z.nativeEnum(AudienceStatus).default(AudienceStatus.Active),
  tags: z.array(TagSchema).optional(),
});

export const BasicSchema = BaseSchema.extend({
  type: z.literal('basic'),
});

export const PreBuiltSchema = BaseSchema.extend({
  mailCode: z.string().nullish(),
  orderNumber: z.string().optional(),
  type: z.literal('pre-built'),
});

export const MasterFileSchema = BaseSchema.omit({ mailCode: true, orderNumber: true }).extend({
  type: z.literal('master-file'),
});

export const SaveFormSchema = z
  .discriminatedUnion('type', [
    AudienceSchema,
    AudiencePreBuiltSchema,
    AudienceMasterFileSchema,
    BasicSchema,
    PreBuiltSchema,
    MasterFileSchema,
  ])
  .superRefine(({ giftDateEnd, giftDateStart }, context) => {
    if (isDefined(giftDateStart)) {
      if (isDefined(giftDateEnd) && isAfter(giftDateStart, giftDateEnd)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          fatal: true,
          message: s(['Site.Pages.AudienceBuilder.Save.Validation.GiftAmount.StartDate']),
          path: ['giftDateStart'],
        });

        return z.NEVER;
      }
    }

    if (isDefined(giftDateEnd)) {
      if (isDefined(giftDateStart) && isBefore(giftDateEnd, giftDateStart)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          fatal: true,
          message: s(['Site.Pages.AudienceBuilder.Save.Validation.GiftAmount.EndDate']),
          path: ['giftDateEnd'],
        });

        return z.NEVER;
      }
    }
  });

export type SaveForm = z.infer<typeof SaveFormSchema>;
export type SaveFormType = z.infer<typeof SaveFormTypeSchema>;
